/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { BlockTypes } from "constants/BlockTypes";
import { FilterSet, selectEnhanceSpeechFilter } from "constants/FilterTypes";
import { FADE_SHIFT_OFFSET_MS } from "PhonosConstants";
import { Asset, AssetSet, MediaType } from "types/Asset";
import { Block, BlockToken } from "types/Block";
import {
  Clip,
  ClipCopy,
  ClipEnhancementInfo,
  ClipId,
  ClipWithAsset,
  ClipWithPlaylistTimes,
  isClipTypeWithAssetSupport,
  isClipWithAsset,
  PlaylistClip,
} from "types/Clip";
import { ModelVersion } from "components/EnhancedSpeech/useEnhanceSpeech/ModelVersion";

type TimeAlterations = {
  duration: number;
  isBackground: boolean;
  backgroundGain?: number;
  fadeIn: boolean;
  fadeOut: boolean;
};

export function getTimeAlterations(clip: Clip): TimeAlterations {
  switch (clip.type) {
    case BlockTypes.WAVEFORM:
      return {
        duration: clip.duration,
        fadeIn: clip.fadeIn,
        fadeOut: clip.fadeOut,
        isBackground: clip.isBackground,
        backgroundGain: clip.backgroundGain,
      };
    case BlockTypes.RECORDING_SESSION:
    case BlockTypes.TRANSCRIPTION:
    case BlockTypes.PLACEHOLDER:
      return {
        duration: clip.duration,
        fadeIn: false,
        fadeOut: false,
        isBackground: false,
      };
    default:
      throw new TypeError(`Unknown clip type: ${clip}`);
  }
}
export function mapClipsToClipsWithTimes(
  clips: Clip[]
): ClipWithPlaylistTimes[] {
  let runningStartTime = 0.0;
  let previousTimeAlterations: TimeAlterations | null;

  return clips.map((clip) => {
    const timeAlterations = getTimeAlterations(clip);
    let startTimeOffset = 0;

    // If necessary, shift current clip start time backwards to overlap with end of the previous asset track
    if (
      timeAlterations.fadeIn ||
      (previousTimeAlterations?.fadeOut &&
        !previousTimeAlterations?.isBackground)
    ) {
      // Don't shift backwards too far. Don't shift past:
      // - the beginning of the playlist
      // - the beginning of the previous clip
      // - the end of the current clip
      // - half the previous clip if it is both fade-in and fade-out
      // - half the current clip if it is both fade-in and fade-out

      const maxCurrentClipShiftLength =
        timeAlterations.fadeIn && timeAlterations.fadeOut
          ? clip.duration / 2
          : clip.duration;

      // If previousAssetClip is null, we don't want to factor it in to the calculation. Since null is
      // treated as 0 in the context of Math.min(), we set it to Infinity instead so it will never be the
      // minimum. Additionally, the duration of a ClipWithAssetSupport clip should never legitimately be 0.
      const previousAssetDuration =
        previousTimeAlterations?.duration ?? Infinity;
      const maxPreviousClipShiftLength =
        previousTimeAlterations?.fadeIn && previousTimeAlterations?.fadeOut
          ? previousAssetDuration / 2
          : previousAssetDuration;

      startTimeOffset = -Math.min(
        FADE_SHIFT_OFFSET_MS,
        runningStartTime,
        maxPreviousClipShiftLength,
        maxCurrentClipShiftLength
      );
    }

    const startTime = runningStartTime + startTimeOffset;
    const endTime = startTime + clip.duration;

    // We don't account for fade-out here because we need to know the duration of the next clip to calculate the shift
    runningStartTime = timeAlterations.isBackground
      ? runningStartTime
      : endTime;
    previousTimeAlterations = isClipTypeWithAssetSupport(clip)
      ? timeAlterations
      : previousTimeAlterations;

    return {
      ...clip,
      startTime,
      endTime,
    };
  });
}

export function mapClipsToPlaylist(
  clips: Clip[],
  assets: AssetSet,
  filters: FilterSet | undefined,
  enhanceSpeechModelVersion: ModelVersion | undefined
): PlaylistClip[] {
  const {
    enabled: isStudioSoundEnabled,
    strengthPercent: enhanceStrengthPercent,
    backgroundStrengthPercent,
  } = selectEnhanceSpeechFilter(filters);

  return mapClipsToClipsWithTimes(clips)
    .filter(isClipWithAsset)
    .map((clip: ClipWithPlaylistTimes & ClipWithAsset): PlaylistClip => {
      const asset = assets[clip.assetId];
      const assetId = clip.assetId;
      const isTranscription = clip.type == BlockTypes.TRANSCRIPTION;
      const enhancedAssetId = isTranscription
        ? asset?.streamingAudioV2Url
          ? assetId
          : (asset?.studioSoundTrackId ?? undefined)
        : undefined;
      const calculatedEnhanceStrengthPercent = isTranscription
        ? isStudioSoundEnabled
          ? enhanceStrengthPercent
          : 0
        : undefined;
      const calculatedBackgroundStrengthPercent = isTranscription
        ? isStudioSoundEnabled
          ? backgroundStrengthPercent
          : 0
        : undefined;

      const enhancementInfo: ClipEnhancementInfo | undefined =
        enhancedAssetId &&
        calculatedEnhanceStrengthPercent !== undefined &&
        calculatedBackgroundStrengthPercent !== undefined
          ? ({
              assetId: enhancedAssetId,
              strengthPercent: calculatedEnhanceStrengthPercent,
              backgroundStrengthPercent: calculatedBackgroundStrengthPercent,
              enhanceSpeechModelVersion: enhanceSpeechModelVersion,
              isStudioSoundEnabled: isStudioSoundEnabled,
            } as ClipEnhancementInfo)
          : undefined;

      return {
        ...clip,
        assetId,
        enhancementInfo,
        mediaType: asset?.mediaType ?? MediaType.AUDIO,
      };
    });
}

export function calculateTotalDuration(
  playlist: ClipWithPlaylistTimes[]
): number {
  return playlist.length === 0
    ? 0
    : Math.max(...playlist.map((clip) => clip.endTime));
}

export interface Range {
  startTime: number;
  endTime: number;
}

export const rangeFromToken = (token: BlockToken): Range => {
  return {
    startTime: token.playlistStartTime,
    endTime: token.playlistEndTime,
  };
};

export interface Selection extends Range {
  clipIds: ClipId[];
}

export function rangesIntersect(range1: Range, range2: Range): boolean {
  return (
    range1.startTime <= range2.endTime && range2.startTime <= range1.endTime
  );
}

export function rangesIntersectExcludeAdjacent(
  range1: Range,
  range2: Range
): boolean {
  return range1.startTime < range2.endTime && range2.startTime < range1.endTime;
}

export function buildClipCopyFromSelection(
  selection: Selection,
  clip: ClipWithPlaylistTimes
): ClipCopy {
  switch (clip.type) {
    case BlockTypes.PLACEHOLDER:
      return clip;
    case BlockTypes.WAVEFORM:
    case BlockTypes.TRANSCRIPTION:
      const copyStartTime = Math.max(clip.startTime, selection.startTime);
      const copyEndTime = Math.min(clip.endTime, selection.endTime);

      const offset = Math.min(
        copyStartTime - clip.startTime + clip.offset,
        clip.offset + clip.duration
      );
      const duration = Math.max(copyEndTime - copyStartTime, 0);

      const { id, startTime, endTime, ...rest } = clip;
      return {
        ...rest,
        offset,
        duration,
      };
    default:
      console.assert(
        false,
        `Encountered invalid block type [${clip.type}] for clip ID [${clip.id}]`
      );
      return clip;
  }
}

export function clipWithoutTimes(clipWithTime: ClipWithPlaylistTimes): Clip {
  const { startTime, endTime, ...rest } = clipWithTime;
  return rest;
}

export function findClipsInRange(
  clips: Clip[],
  range: Range
): ClipWithPlaylistTimes[] {
  const clipsWithTimes = mapClipsToClipsWithTimes(clips);
  return clipsWithTimes.filter((clip) => rangesIntersect(range, clip));
}

export function findClipsInSelection(
  clips: Clip[],
  selection: Selection
): ClipWithPlaylistTimes[] {
  return findClipsInRange(clips, selection).filter((clip) =>
    selection.clipIds.includes(clip.id)
  );
}

export function clipsFromBlock(block: Block, clips: Clip[]): Clip[] {
  const clipsInBlock = clips.filter((clip) => {
    return block.clipIds.includes(clip.id);
  });
  return clipsInBlock;
}
